import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { bookingUrl, type BookingPageView } from "@goyone/shared";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { Icon } from "./Icon";
import { Modal } from "./ui";
import { useToast } from "./Toast";
import { MailCompose } from "./MailCompose";

/**
 * إرسال رابط حجزٍ إلى عميل — «اختر وقتك».
 *
 * المواعيد كانت تُتفَق عليها بمراسلةٍ ذهابًا وإيابًا: «هل يناسبك الثلاثاء
 * العاشرة؟» ثم «لا، الأربعاء؟». والرابط يقلب الاتجاه: العميل يرى الشاغر
 * بنفسه فيختار، فيقع الموعد في التقويم بلا وسيط.
 *
 * والنافذة تعرض صفحات الحجز النشطة: لكلٍّ رابطُها يُنسخ، وبريدٌ يُكتب
 * بنصٍّ مهيَّأ لمن يُعرف بريده. وحين لا صفحة تقول ذلك وتدلّ على بابها،
 * فالزرّ لا يَعِد بما لا يملك.
 */
export function BookingLinkButton({
  className = "btn btn-sm",
  label = "رابط حجز",
  /** بريد العميل — به يُهيَّأ زرّ الإرسال، وبغيابه يبقى النسخ وحده. */
  email,
  /** السجلّ الذي تُنسب إليه الرسالة فتدخل تاريخه. */
  link,
}: {
  className?: string;
  label?: string;
  email?: string | null;
  link?: {
    leadId?: string | null;
    accountRefId?: string | null;
    contactId?: string | null;
  };
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        className={className}
        onClick={() => setOpen(true)}
        title="أرسل للعميل رابطًا يختار به موعده"
      >
        <Icon name="calendar" size={14} />
        {label}
      </button>

      {open && (
        <BookingLinkModal
          email={email}
          link={link}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function BookingLinkModal({
  email,
  link,
  onClose,
}: {
  email?: string | null;
  link?: {
    leadId?: string | null;
    accountRefId?: string | null;
    contactId?: string | null;
  };
  onClose: () => void;
}) {
  const toast = useToast();
  const session = useSession();
  const [mailing, setMailing] = useState<{
    url: string;
    page: BookingPageView;
  } | null>(null);

  const pages = useQuery({
    queryKey: ["booking", "pages"],
    queryFn: () => api<BookingPageView[]>("/booking/pages"),
  });

  const active = (pages.data ?? []).filter((p) => p.isActive);
  const urlOf = (p: BookingPageView) =>
    bookingUrl(window.location.origin, session.tenant.slug, p.slug);

  return (
    <>
      <Modal title="أرسل رابط حجز" onClose={onClose}>
        {pages.isPending && <div className="skeleton" style={{ height: 90 }} />}

        {pages.data && active.length === 0 && (
          <p className="mute small">
            لا صفحات حجزٍ نشطة بعد —{" "}
            <Link to="/sales/booking" className="link" onClick={onClose}>
              أنشئ صفحة
            </Link>{" "}
            على تقويمٍ لديك، فيصير لك رابطٌ يختار العميل موعده منه.
          </p>
        )}

        <div className="pick-list">
          {active.map((p) => (
            <div className="pick-row" key={p.id} style={{ cursor: "default" }}>
              <span className="pick-main">
                <span style={{ fontWeight: 700 }}>{p.name}</span>
                <span className="tiny mute">
                  {p.durationMin} دقيقة
                  {p.price != null
                    ? ` — ${p.price.toLocaleString("en-US")} ج.م`
                    : ""}
                  {p.calendarName ? ` · ${p.calendarName}` : ""}
                </span>
              </span>

              <span className="row" style={{ gap: 4 }}>
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    void navigator.clipboard?.writeText(urlOf(p));
                    toast("نُسخ الرابط — ألصقه في رسالتك");
                  }}
                >
                  <Icon name="link" size={13} />
                  انسخ
                </button>
                {/* البريد يُكتب من صندوق الشركة ويُنسب إلى سجلّ العميل. */}
                {email && (
                  <button
                    className="btn btn-sm"
                    onClick={() => setMailing({ url: urlOf(p), page: p })}
                  >
                    <Icon name="send" size={13} />
                    أرسله بالبريد
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      </Modal>

      {mailing && email && (
        <MailCompose
          to={email}
          link={link}
          subject={`احجز موعدك — ${mailing.page.name}`}
          body={[
            "مرحبًا،",
            "",
            `يسعدنا لقاؤك. اختر الوقت الذي يناسبك من هنا (${mailing.page.durationMin} دقيقة):`,
            mailing.url,
            "",
            "وسيصلك تأكيدٌ فور اختيارك.",
          ].join("\n")}
          onClose={() => setMailing(null)}
          onSent={() => {
            setMailing(null);
            onClose();
            toast("أُرسل الرابط — والرسالة في سجلّ العميل");
          }}
        />
      )}
    </>
  );
}
